import FilePreviewComponent from '@/components/probaworkflows_base_components/FilePreviewComponent/FilePreviewComponent.vue';
import RecursiveDependentQuestions from '@/components/probaworkflows_base_components/RecursiveDependentQuestions/RecursiveDependentQuestions.vue';
import {
  CLEAR_FORM_PROGRESS,
  COMPONENT_RENDERING_URL,
  GET_APPLICATION_FORMS,
  GET_FORM_PROGRESS,
  get_headers,
  get_project_id,
  get_user_role,
  REQUEST_HELP,
  REQUEST_INFO_URL,
  SAVE_FORM_PROGRESS,
  SUBMIT_APPLICATION_FORMS_ANSWERS,
  VERIFY_SECTION_URL,
} from '@/utils/constants/constants';
import alert_mixin from "@/utils/mixins/alert_mixin";
import entity_form_depquestions_mixins from '@/utils/mixins/entity_form_mixins/entity_form_depquestions_mixins';
import entity_form_files_mixin from '@/utils/mixins/entity_form_mixins/entity_form_files_mixin';
import entity_form_scroll_mixin from '@/utils/mixins/entity_form_mixins/entity_form_scroll_mixin';
import entity_form_utils_mixins from '@/utils/mixins/entity_form_mixins/entity_form_utils_mixins';
import save_sections from '@/utils/mixins/save_sections';
import {
  validate_non_empty_string,
  validate_non_sub_zero_number
} from '@/utils/validations/validate_inputs';
import FacialBiometric from "@/views/probaworkflows_base/verification/facial_biometric/FacialBiometric.vue";
import axios from 'axios';
import { reactive } from 'vue';

export default {
  name: "MultiSectionForm",
  mixins: [alert_mixin, save_sections, entity_form_scroll_mixin, entity_form_files_mixin, entity_form_depquestions_mixins, entity_form_utils_mixins],
  components: {
    FacialBiometric,
    FilePreviewComponent,
    RecursiveDependentQuestions
  },
  data: () => ({
    preview_url: null,
    user_role: null,
    current_section_index: 0,
    completed_sections: [], // track saved sections
    headers: '',
    safe_form_config: {},
    safe_form_data: reactive({}),
    request_info_note: '',
    selected_request_fields: [],
    answered_file_questions: [],
    application_form_id: null,
    active_section: null,
    active_section_name: null,
    is_submitting: false,
    file_validation_error: {},
    validation_errors: {},
    show_file_preview_modal: false,
    preview_name: '',
    preview_type: 'other',
    preview_modal: false,
    submit_attempted: false,
    is_form_loading: false,
    first_invalid_field_model: null,
    request_help_note: '',
    is_sending: false,
    entity_type_name: null,
    user_id: null,
    show_profile_completion_gate: false,
    profile_completion_gate_message: '',
    inline_form_notice_text: '',
    inline_form_notice_type: 'info',
    show_request_info_modal: false,

    is_help_modal_open: false,
    unanswered_questions_list: [],
    support_email: null,
  }),
  computed: {
    progress_percentage() {
      if (!this.safe_form_config.sections || this.safe_form_config.sections.length <= 1) return '0%';
      return `${((this.current_section_index + 1) / this.safe_form_config.sections.length) * 100}%`;
    },

    is_last_section() {
      if (!this.safe_form_config.sections?.length) return false;
      const lastIndex = this.safe_form_config.sections.length - 1;

      // fast path: index matches
      if (this.current_section_index === lastIndex) return true;

      // DOM fallback: check if the last section is mostly visible inside the carousel
      const container = this.$refs.carousel;
      if (!container) return false;
      const lastSection = container.querySelector(`.form-wrapper[data-index="${lastIndex}"]`);
      if (!lastSection) return false;

      const containerRect = container.getBoundingClientRect();
      const rect = lastSection.getBoundingClientRect();
      const isVertical = this.get_scroll_axis(container) === "vertical";

      const overlap = isVertical
        ? Math.max(0, Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top))
        : Math.max(0, Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left));

      const size = isVertical ? rect.height : rect.width;
      // require at least 50% visibility to count as "last"
      return size > 0 && (overlap / size) >= 0.5;
    }
  },
  mounted() {
    this.headers = get_headers();
    this.get_form_to_preview();
    this.application_form_id = Number(sessionStorage.getItem('application_form_id')) || null;

    // wait for DOM to be rendered
    this.$nextTick(() => {
      const carousel = this.$refs.carousel;
      if (!carousel) return;
      // add scroll listener with rAF debounce
      carousel.addEventListener('scroll', this._onCarouselScroll, { passive: true });
      // mobile touchend fallback after swipe
      carousel.addEventListener('touchend', this._onCarouselTouchEnd, { passive: true });
      // listen to resize (orientation change)
      window.addEventListener('resize', this._onWindowResize);
      // initial measurement
      this.handle_scroll();
    });
    // IntersectionObserver optional backup (kept for extra reliability)
    this.observe_sections();
  },
  beforeUnmount() {
    const carousel = this.$refs.carousel;
    if (carousel) {
      carousel.removeEventListener('scroll', this._onCarouselScroll);
      carousel.removeEventListener('touchend', this._onCarouselTouchEnd);
    }
    window.removeEventListener('resize', this._onWindowResize);
    // cancel any pending rAF
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
    if (this._touchTimeout) {
      clearTimeout(this._touchTimeout);
      this._touchTimeout = null;
    }

    if (this._observer) {
      try { this._observer.disconnect(); } catch (e) { /* ignore */ }
      this._observer = null;
    }
  },
  methods: {
    async navigate_to_first_available_sidebar_url() {
      const role = get_user_role();
      const project_id = get_project_id();
      const base_entity_type_id = sessionStorage.getItem('base_entity_type_id');
      const params = { role, project_id, base_entity_type_id };

      try {
        const response = await axios.get(COMPONENT_RENDERING_URL, {
          headers: this.headers,
          params,
        });
        const menu_items = response.data?.data || [];
        const first_url = this.find_first_sidebar_url(menu_items);
        const current_path = this.$route?.path || '';
        const fallback_url = current_path === '/entity_overview' ? '/overview_of_profile' : '/entity_overview';
        const target_url = !first_url || first_url === current_path
          ? fallback_url
          : first_url;
        this.$router.push(target_url);
      } catch (error) {
        this.$router.push('/entity_overview');
      }
    },

    find_first_sidebar_url(components, parent_type = null) {
      const component_list = Array.isArray(components) ? components : [];
      for (const component of component_list) {
        const component_url = component?.url;
        if (component_url && component_url.trim() !== '') {
          const item_type = parent_type === 'sub' ? 'child' : 'sub';
          sessionStorage.setItem(
            'sidebar_active_item_key',
            `${item_type}:${component.id}`
          );
          return component_url;
        }

        const sub_components = this.find_first_sidebar_url(
          component?.sub_components,
          'component'
        );
        if (sub_components) {
          return sub_components;
        }

        const child_components = this.find_first_sidebar_url(
          component?.child_components,
          'sub'
        );
        if (child_components) {
          return child_components;
        }
      }
      return null;
    },

    on_update_field({ key, value }) {
      Reflect.set(this.safe_form_data, key, value);
    },
    set_active_section(section_index) {
      const is_valid_index =
        Number.isInteger(section_index) &&
        section_index >= 0 &&
        section_index < (this.safe_form_config?.sections?.length || 0);
      if (!is_valid_index) {
        return;
      }
      this.current_section_index = section_index;
    },
    close_request_info_modal() {
      this.show_request_info_modal = false;
    },
    proceed_to_complete_application() {
      this.show_profile_completion_gate = false;
    },
    clear_validation_for_model(model) {
      const next_errors = { ...this.validation_errors };
      Reflect.deleteProperty(next_errors, model);
      this.validation_errors = next_errors;
      if (this.first_invalid_field_model === model) {
        this.first_invalid_field_model = null;
      }
    },
    find_field_entry_by_section_question_id(section_question_id) {
      const ordered_entries = this.get_ordered_field_entries();
      const entry = ordered_entries.find(
        candidate => candidate.field?.section_question_id === section_question_id
      );
      return entry || null;
    },
    collect_field_entries(nodes, section_index, entries) {
      const field_nodes = Array.isArray(nodes) ? nodes : [];
      field_nodes.forEach((field) => {
        entries.push({ section_index, field });
        const child_nodes = Array.isArray(field?.dependent_questions)
          ? field.dependent_questions.filter(child => child?.is_visible !== false)
          : [];
        if (child_nodes.length) {
          this.collect_field_entries(child_nodes, section_index, entries);
        }
      });
    },
    get_ordered_field_entries() {
      const entries = [];
      const sections = Array.isArray(this.safe_form_config?.sections)
        ? this.safe_form_config.sections
        : [];
      sections.forEach((section, section_index) => {
        const fields = Array.isArray(section?.fields) ? section.fields : [];
        this.collect_field_entries(fields, section_index, entries);
      });
      return entries;
    },
    async focus_first_invalid_field(missing_section_question_ids) {
      const missing_ids = Array.isArray(missing_section_question_ids)
        ? missing_section_question_ids
        : [];
      const entries = this.get_ordered_field_entries();
      const first_entry = entries.find(
        entry => missing_ids.includes(entry.field?.section_question_id)
      );
      if (!first_entry) {
        return;
      }

      this.current_section_index = first_entry.section_index;
      await this.$nextTick();
      this.scroll_to_section_centered(first_entry.section_index);
      setTimeout(() => {
        const field = first_entry.field;
        const target_section = this.$refs.carousel?.querySelector(
          `.form-wrapper[data-index="${first_entry.section_index}"]`
        );
        const target_element = this.find_invalid_field_element(
          field,
          target_section
        );
        if (!target_element) {
          return;
        }

        target_element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof target_element.focus === 'function') {
          target_element.focus();
        }
      }, 350);
    },
    find_invalid_field_element(field, target_section = null) {
      const target_id = field?.id || field?.model;
      const selectors = [
        target_id,
        `${target_id}-0`,
        field?.model,
      ];
      const lookup_roots = [target_section, document].filter(Boolean);
      for (const root of lookup_roots) {
        for (const selector_id of selectors) {
          if (!selector_id) {
            continue;
          }

          const selector = `#${CSS.escape(String(selector_id))}`;
          const element = root.querySelector(selector);
          if (element) {
            return element;
          }
        }
      }
      return null;
    },
  apply_final_submission_validation(missing_section_question_ids) {
      const missing_ids = Array.isArray(missing_section_question_ids)
        ? missing_section_question_ids
        : [];
      const next_errors = {};
      const entries = this.get_ordered_field_entries();
      let first_invalid_model = null;
      entries.forEach((entry) => {
        const section_question_id = entry.field?.section_question_id;
        if (missing_ids.includes(section_question_id)) {
          Reflect.set(next_errors, entry.field.model, 'This field is mandatory');
          if (!first_invalid_model) {
            first_invalid_model = entry.field.model;
          }
        }
      });
      this.validation_errors = next_errors;
      this.first_invalid_field_model = first_invalid_model;
    this.submit_attempted = true;
    this.focus_first_invalid_field(missing_ids);
  },
  normalize_error_data(error_data) {
    const source_data = error_data || {};
    if (!Array.isArray(source_data)) {
      return source_data;
    }

    const normalized_data = {};
    source_data.forEach((entry) => {
      if (!Array.isArray(entry) || entry.length < 2) {
        return;
      }
      const entry_key = entry[0];
      const entry_value = entry[1];
      Reflect.set(normalized_data, entry_key, entry_value);
    });
    return normalized_data;
  },
    build_single_field_submission(field) {
      const answers = [];
      const file_uploads = [];
      const field_value = Reflect.get(this.safe_form_data, field.model);

      if (field.type === 'file' || field.type === 'facial_verification') {
        const comment_text = Reflect.get(
          this.safe_form_data,
          field.model + '_file_comment'
        );
        const question_comments = comment_text
          ? [{ comment: comment_text, user_id: this.user_id }]
          : null;

        if (field_value?.file) {
          file_uploads.push({ file: field_value.file, id: field.section_question_id });
          answers.push({
            section_question_id: field.section_question_id,
            answer_text: field_value.file.name,
            question_comments: null,
          });
        } else if (field_value?.original_url) {
          answers.push({
            section_question_id: field.section_question_id,
            answer_text: field_value.original_url,
            question_comments: null,
          });
        } else if (field_value?.file_name) {
          answers.push({
            section_question_id: field.section_question_id,
            answer_text: field_value.file_name,
            question_comments: null,
          });
        } else if (question_comments) {
          answers.push({
            section_question_id: field.section_question_id,
            answer_text: null,
            question_comments,
          });
        }
      } else if (field.type === 'multi_select' || field.type === 'single_select' || field.type === 'radio') {
        answers.push({
          section_question_id: field.section_question_id,
          answer_text: this.get_choice_text_from_value(field, field_value),
        });
      } else {
        answers.push({
          section_question_id: field.section_question_id,
          answer_text: (field_value || '').toString().trim(),
        });
      }

      const submission = { answers, file_uploads };

      return submission;
    },
    async auto_save_field(field) {
      if (this.is_submitting || !this.application_form_id || !field?.section_question_id) {
        return;
      }

      this.clear_validation_for_model(field.model);
      const submission = this.build_single_field_submission(field);
      const has_answers = submission.answers.length > 0;
      const has_files = submission.file_uploads.length > 0;
      if (!has_answers && !has_files) {
        return;
      }

      const form_data = new FormData();
      submission.file_uploads.forEach((file_upload) => {
        form_data.append(`file_${file_upload.id}`, file_upload.file);
      });
      form_data.append('answers', JSON.stringify(submission.answers));
      form_data.append('application_form_id', this.application_form_id);
      form_data.append('current_section_index', this.current_section_index.toString());
      form_data.append('is_final_submission', 'false');

      try {
        await axios.post(SUBMIT_APPLICATION_FORMS_ANSWERS, form_data, {
          headers: {
            ...this.headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        this.clear_validation_for_model(field.model);
      } catch (error) {
        console.warn('Field autosave failed', error);
      }
    },
    async handle_file_upload_and_auto_save(event, field, is_dependent = false) {
      await this.handle_file_upload(event, field.section_question_id, is_dependent);
      await this.auto_save_field(field);
    },
    handleNumberInput(event, field) {
      const value = event.target.value.replace(/\D/g, '')
      Reflect.set(this.safe_form_data, field.model, value)
      event.target.value = value
    },
    // Debounced scroll handler using rAF
    _onCarouselScroll() {
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = requestAnimationFrame(() => {
        this.handle_scroll();
        this._scrollRaf = null;
      });
    },

    // Touchend fallback: many mobile browsers complete the swipe after touchend
    _onCarouselTouchEnd() {
      if (this._touchTimeout) clearTimeout(this._touchTimeout);
      // short delay so the browser finishes inertia scroll
      this._touchTimeout = setTimeout(() => {
        this.handle_scroll();
        this._touchTimeout = null;
      }, 80);
    },

    _onWindowResize() {
      // re-evaluate visible section on resize/orientation change
      if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        this.handle_scroll();
        this._resizeTimeout = null;
      }, 120);
    },

    async save_progress_to_server(section_index = null) {
      const current_section_index = Number.isInteger(section_index)
        ? section_index
        : this.current_section_index;
      await axios.post(SAVE_FORM_PROGRESS, {
        application_form_id: this.application_form_id,
        current_section_index
      }, {
        headers: this.headers
      });
    },

    load_progress_from_server() {
      axios.get(GET_FORM_PROGRESS, {
          headers: this.headers,
          params: {
            application_form_id: this.application_form_id
          }
      })
      .then((response) => {
        if (
          response.data.status === 'success' &&
          response.data.current_section_index !== undefined
        ) {
          const savedSectionIndex = response.data.current_section_index;

          if (
            savedSectionIndex >= 0 &&
            savedSectionIndex < this.safe_form_config.sections.length
          ) {
            this.current_section_index = savedSectionIndex;
            this.$nextTick(() => {
              this.scroll_to_section(savedSectionIndex);
            });
          }
        }
      })
      .catch((error) => {
        console.warn(error)
        // Silently ignore errors - this is expected when user has no entity profile yet
      });
    },

    async clear_progress() {
      await axios.delete(CLEAR_FORM_PROGRESS, {
        headers: this.headers,
        data: {
          application_form_id: this.application_form_id
        }
      });
    },

    get_file_action_text(fileData) {
      return this.is_word_document(fileData) ? 'Download' : 'Preview';
    },

    guess_preview_type(filename = '', mime = '') {
      const name = String(filename || '').toLowerCase();
      const m = String(mime || '').toLowerCase();
      // Word documents
      if (m.includes('word') || /\.(doc|docx)$/.test(name)) return 'word';
      // Images
      if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)) return 'image';
      // PDF
      if (m === 'application/pdf' || /\.pdf$/.test(name)) return 'pdf';
      // Videos
      if (m.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/.test(name)) return 'video';
      return 'other';
    },

    is_word_document(fileData) {
      if (!fileData) return false;
      const filename = fileData.file_name || '';
      const file = fileData.file;
      if (file instanceof File) {
        return this.guess_preview_type(file.name, file.type) === 'word';
      }
      return /\.(doc|docx)$/i.test(filename);
    },

    approve(section_id) {
      const data = {
        form_section_id: section_id,
        is_verified: true,
        application_form_id: parseInt(this.application_form_id, 10),
        verification_comment: "",
      };
      axios.post(VERIFY_SECTION_URL, data, {
        headers: this.headers
      })
        .then(response => {
          const response_data = response.data;
          if (response_data.status === 'success') {
            this.get_form_to_preview();
            this.show_success("Section verified successfully.")
          } else {
            this.show_error('Error during the form submit');
          }
        })
        .catch(error => {
          if (error.response && error.response.data) {
            this.show_error(error.response.data.message);
          }
        });
    },

    toggle_requested_field(event, field) {
      if (event.target.checked) {
        this.selected_request_fields.push({
          form_section_id: this.active_section,
          form_section_name: this.active_section_name,
          requested_field_id: field.section_question_id,
          requested_field_text: field.question_text
        });
      } else {
        this.selected_request_fields = this.selected_request_fields.filter(
          selected => selected.requested_field_id !== field.section_question_id
        );
      }
    },

    async show_help_request_modal() {
      this.unanswered_questions_list = [];
      let text_buffer = "The following questions are incomplete:\n\n";
      const config = this.safe_form_config;

      config.sections.forEach((section) => {
          section.fields.forEach(field => {
              const value = Reflect.get(this.safe_form_data, field.model);
              const is_empty = (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
              if (is_empty) {
                  const question_info = {
                      label: field.label,
                      type: field.type,
                      section: section.title,
                      model: field.model,
                      isRequired: field.required,
                      selected_for_help: false,
                  };
                  this.unanswered_questions_list.push(question_info);
                  //Add to the text buffer for the textarea
                  const importance = field.required ? '*' : '  ';
                  text_buffer += `${importance}  ${section.title}: ${question_info.label}\n`;
              }
          });
      });

    if (this.unanswered_questions_list.length === 0) {
        this.formatted_unanswered_text = "All questions in all sections have been answered!";
    } else {
        this.formatted_unanswered_text = text_buffer;
    }

    this.is_help_modal_open = true;
},

    async request_information() {
      if (!this.validate_request_information_inputs()) return;
      const application_form_id = parseInt(this.safe_form_config.formId, 10);
      const message = this.request_info_note;
      const requested_fields = this.selected_request_fields;
      const data = {
        application_form_id: application_form_id,
        message: message,
        requested_fields: requested_fields,
      };

      const response = await axios.post(REQUEST_INFO_URL, data, {
        headers: this.headers
      });

      if (response.data.status === 'success') {
        this.show_success("Information successfully requested");
      } else {
        this.show_error('Error during the form submit');
      }
    },

    validate_request_information_inputs() {
      if (!validate_non_empty_string(this.request_info_note)) {
        this.show_error('Request information note must not be null');
        return false;
      }
      if (!validate_non_sub_zero_number(parseInt(this.application_form_id, 10))) {
        this.show_error('Profile role form id must not be null');
        return false;
      }
      return true;
    },

    _init_dependent_form_data(dep_list = []) {
      (dep_list || []).filter(Boolean).forEach((dependent_question) => {
        Reflect.set(
          this.safe_form_data,
          dependent_question.model,
          dependent_question.type === "multi_select" ? (dependent_question.value || []) : (dependent_question.value || "")
        );

        if (dependent_question.type === "file") {
          const commentKey = dependent_question.model + "_file_comment";
          if (!Reflect.has(this.safe_form_data, commentKey)) {
            Reflect.set(this.safe_form_data, commentKey, "");
          }
        }

        if (dependent_question.dependent_questions?.length) {
          this._init_dependent_form_data(dependent_question.dependent_questions);
        }
      });
    },

    index_all_questions(list, map) {
      (list || []).forEach(q => {
        if (!q) return;

        if (
          q.dependant_section_question_id &&
          !map[q.dependant_section_question_id]
        ) {
          map[q.dependant_section_question_id] = {
            section_question_id: q.dependant_section_question_id,
            question_text: q.dependant_question_text,
            question_type: q.dependant_question_type || { question_type: 'short_text' },
            answer: q.answer ?? null,
            multi_select_choices: q.multi_select_choices || [],
            is_mandatory: q.is_mandatory,
          };
        }

        this.index_all_questions(q.dependant_questions || [], map);
      });
    },


        async get_form_to_preview() {
          this.is_form_loading = true;

          try {
          const resp = await axios.get(GET_APPLICATION_FORMS, { headers: this.headers });

          this.user_id = resp.data.user_id;

          const payload = Array.isArray(resp.data.data)
            ? resp.data.data[0]
            : resp.data.data;

      this.support_email = payload.support_email;
      this.application_form_id = payload.application_form_id;
      this.user_role = resp.data.logged_in_user_role;

          const all_questions_map = {};

          payload.form.sections.forEach(section =>
            section.questions.forEach(question => {
              Reflect.set(all_questions_map, question.section_question_id, question);

              //complexity reduced here
              this.index_all_questions(
                question.dependant_questions || [],
                all_questions_map
              );
            })
          );

          this.safe_form_config = {
            submission_id: payload.application_form_id,
            form_title: payload.form_name,
            formId: payload.form_id,
            sections: payload.form.sections
              .filter(section => section.questions?.length)
              .map(section => ({
                id: section.id,
                is_verified: section.is_verified,
                title: section.name,
                fields: section.questions
                  .filter(q => !q.is_dependent)
                  .map(q => {
                    const option_map = this.build_option_map(q.multi_select_choices);

                    const value = this.deserialize_answer(
                      q.answer,
                      q.question_type.question_type,
                      option_map
                    );

                    return {
                      label: q.question_text,
                      model: `q_${q.section_question_id}`,
                      id: `q_${q.section_question_id}`,
                      section_question_id: q.section_question_id,
                      type: this.map_question_type(q.question_type.question_type),
                      required: q.is_mandatory,
                      placeholder: q.question_text,
                      description: q.question_description,
                      options: (q.multi_select_choices || []).map(c => ({
                        text: c.choice,
                        value: c.choice
                      })),
                      value,
                      dependent_questions: (q.dependant_questions || [])
                        .map(dq => this.build_dependent_question(dq, all_questions_map))
                        .filter(Boolean),
                      is_dependent: false
                    };
                  })
              }))
          };

          Object.keys(this.safe_form_data).forEach(key => {
            Reflect.set(this.safe_form_data, key, undefined);
          });

          this.safe_form_config.sections.forEach(section =>
            section.fields.forEach(field => {
              Reflect.set(
                this.safe_form_data,
                field.model,
                field.type === 'multi_select'
                  ? field.value || []
                  : field.value || ''
              );

              this._init_dependent_form_data(field.dependent_questions || []);
            })
          );

          this.$nextTick(() => {
            this.setup_dependent_question_listeners();
            this.load_progress_from_server();
          });
          } catch (error) {
            const error_message = error.response?.data?.message || error.message;
            this.show_error(error_message || 'Failed to load onboarding form');
          } finally {
            this.is_form_loading = false;
          }
        },

    async save_section(proceed_in_draft_mode = false, section_index = null) {
      const active_section_index = Number.isInteger(section_index)
        ? section_index
        : this.current_section_index;
      if (this.is_submitting) {
        return;
      }
      this.submit_attempted = true;
      this.is_submitting = true;
      const config = this.safe_form_config;
      const form_data = new FormData();
      const answers = [];
      const file_uploads = [];
      const current_section = Reflect.get(config.sections, active_section_index);

      this.validation_errors = {};

      this._collect_answers_recursive(current_section.fields, answers, file_uploads);

      file_uploads.forEach(fu => {
        if (fu && fu.id && fu.file) {
          const key = `file_${String(fu.id)}`;
          form_data.append(key, fu.file);
        }
      });

      form_data.append("answers", JSON.stringify(answers));
      form_data.append("application_form_id", this.application_form_id);
      form_data.append("current_section_index", active_section_index.toString());
      form_data.append("is_final_submission", "false");

      try {
        const res = await axios.post(
          SUBMIT_APPLICATION_FORMS_ANSWERS,
          form_data, { headers: { ...this.headers, "Content-Type": "multipart/form-data" } }
        );

        if (res.data && res.data.status === "success") {
          this.show_success("Section saved successfully");
          // Mark section as completed
          if (!this.completed_sections.includes(active_section_index)) {
            this.completed_sections.push(active_section_index);
          }
          // Save progress to server
          await this.save_progress_to_server(active_section_index);
          // Auto-scroll to next section if available
          if (proceed_in_draft_mode) {
            await this.navigate_to_first_available_sidebar_url();
          } else if (active_section_index < this.safe_form_config.sections.length - 1) {
            this.current_section_index = active_section_index + 1;
            // Wait for DOM update then scroll to center the next section
            this.$nextTick(() => {
              this.scroll_to_section_centered(this.current_section_index);
            });
          }
          return true;
        } else {
          const msg = (res.data && res.data.message) || "Section submission failed";
          this.show_error(msg);
          return false;
        }
      } catch (error) {
        this.show_error("Error saving section");
        return false;
      } finally {
        this.is_submitting = false;
      }
    },

    async proceed_in_draft_mode(section_index = null) {
      const result = await this.save_section(true, section_index);
      return result;
    },


    should_skip_answer_node(node, seen) {
      if (!node) {
        return true;
      }

      if (node.is_dependent && !node.is_visible) {
        return true;
      }

      const section_question_id = node.section_question_id;
      if (section_question_id && seen.has(section_question_id)) {
        return true;
      }

      if (section_question_id) {
        seen.add(section_question_id);
      }

      return false;
    },

    collect_answer_node(node, answers, file_uploads, seen) {
      if (this.should_skip_answer_node(node, seen)) {
        return;
      }

      this.process_field(node, answers, this.user_id, file_uploads);

      const dependent_questions = Array.isArray(node.dependent_questions)
        ? node.dependent_questions
        : [];

      if (!dependent_questions.length) {
        return;
      }

      this._collect_answers_recursive(
        dependent_questions,
        answers,
        file_uploads,
        seen
      );
    },

    _collect_answers_recursive(
      nodes = [],
      answers = [],
      file_uploads = [],
      seen = new Set()
    ) {
      nodes.forEach((node) => {
        this.collect_answer_node(node, answers, file_uploads, seen);
      });
    },

async send_help_email() {
    const selected_questions = this.unanswered_questions_list.filter(question => question.selected_for_help);


    this.is_help_modal_open = false;
    let loader;

    try {
        loader = await this.show_loader("Sending help request...");
        const data = {
            request_help_note: this.request_help_note,
            unanswered_questions: selected_questions,
            application_form_id: this.application_form_id
        };

        const response = await axios.post(REQUEST_HELP, data, { headers: this.headers });
        const payload = response.data;

        if (payload.status === 'success') {
            this.show_success("Your help request has been sent successfully!");

            // Cleanup
            this.request_help_note = '';
            this.unanswered_questions_list.forEach(q => q.selected_for_help = false);
        } else {
            this.show_error(payload.message || "Failed to send help request.");
        }

    } catch (error) {
        const error_message = error.response?.data?.message || error.message;
        this.show_error("Failed to send: " + error_message);

    } finally {
        if (loader) await this.close_loader(loader);
    }
  },

  async submit_answers() {
    if (this.is_submitting) return;
    this.submit_attempted = true;
    this.validation_errors = {};
    this.is_submitting = true;
    const loader = await this.show_loader("Submitting form...");

    try {
      const config = this.safe_form_config;
      const form_data = new FormData();
      const answers = [];
      const file_uploads = [];

      config.sections.forEach(section => {
        this._collect_answers_recursive(section.fields, answers, file_uploads);
      });

      file_uploads.forEach(fu => form_data.append(`file_${fu.id}`, fu.file));
      form_data.append('answers', JSON.stringify(answers));
      form_data.append('application_form_id', config.submission_id);
      form_data.append('current_section_index', (config.sections.length - 1).toString());
      form_data.append('is_final_submission', 'true');

      const request_config = {
        headers: {
          ...this.headers,
          'Content-Type': 'multipart/form-data'
        }
      };

      const res = await axios.post(
        SUBMIT_APPLICATION_FORMS_ANSWERS,
        form_data,
        request_config
      );

      if (res.data.status !== 'success') {
        this.show_error(res.data.message || "Form submission failed");
        return;
      }

      this.show_success('Form submitted successfully');
      await axios.post(SAVE_FORM_PROGRESS, {
        application_form_id: this.application_form_id,
        current_section_index: config.sections.length - 1,
        mark_completed: true
      }, {
        headers: this.headers
      });
      this.$router.push('/entity_overview');
    } catch (error) {
      const response_data = error.response?.data || {};
      const error_data = this.normalize_error_data(response_data.errors);
      const missing_section_question_ids = Array.isArray(error_data?.missing_section_question_ids)
        ? error_data.missing_section_question_ids
        : [];
      const validation_message = Array.isArray(error_data?.message)
        ? error_data.message[0]
        : response_data.message;

      if (missing_section_question_ids.length) {
        this.apply_final_submission_validation(missing_section_question_ids);
        this.show_info(
          validation_message || 'Some mandatory fields were not filled in'
        );
      } else {
        const error_message = response_data.message || error.message;
        this.show_error(error_message || "Form submission failed");
      }
    } finally {
      await this.close_loader(loader);
      this.is_submitting = false;
    }
  },
  }
};
